import React, { useState } from 'react';

const Services = () => {
  const [activeService, setActiveService] = useState(0);
  const [imageHover, setImageHover] = useState(false);

  const colors = {
    bg: '#f8fbff',
    white: '#ffffff',
    navy: '#0b1f33',
    navyLight: '#173b5c',
    blue: '#0284c7',
    blueDark: '#0369a1',
    blueLight: '#e0f2fe',
    cyan: '#38bdf8',
    text: '#334155',
    muted: '#64748b',
    border: '#dbe7f0',
    soft: '#eef7fc',
  };

  const servicesList = [
    {
      id: '01',
      category: 'STRUCTURAL ENGINEERING',
      title: 'Industrial & Commercial Structures',
      shortTitle: 'Structural Engineering',
      description:
        'Complete structural engineering solutions for industrial facilities, warehouses, manufacturing plants, commercial buildings, and complex structural frameworks.',
      features: [
        'Industrial plant structural framing',
        'Heavy equipment support structures',
        'Multi-story commercial buildings',
        'Structural audits & peer reviews',
      ],
      image:
        'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1400&q=85',
      accent: '#0284c7',
      stat: '01',
      label: 'BUILD • ANALYZE • OPTIMIZE',
    },

    {
      id: '02',
      category: 'DIGITAL ENGINEERING',
      title: 'Advanced Analysis & 3D Modeling',
      shortTitle: 'Digital Engineering',
      description:
        'Modern engineering workflows combining structural analysis, connection verification, BIM coordination, and fabrication-ready 3D modeling.',
      features: [
        'STAAD.Pro structural analysis',
        'IDEA StatiCa connection design',
        'RAM Connection verification',
        'Tekla Structures 3D modeling',
      ],
      image:
        'https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=1400&q=85',
      accent: '#0369a1',
      stat: '02',
      label: 'MODEL • ANALYZE • VALIDATE',
    },

    {
      id: '03',
      category: 'LAND DEVELOPMENT',
      title: 'Land Development',
      shortTitle: 'Land Development',
      description:
        'Comprehensive land development planning that transforms site information into practical, coordinated, and construction-ready development plans.',
      features: [
        'Site development planning',
        'Grading & drainage design',
        'Utility layout planning',
        'Civil 3D development models',
      ],
      image:
        'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1400&q=85',
      accent: '#0ea5e9',
      stat: '03',
      label: 'PLAN • GRADE • DEVELOP',
    },

    {
      id: '04',
      category: 'LAND SURVEY',
      title: 'Land Survey',
      shortTitle: 'Land Survey',
      description:
        'Accurate land surveying and mapping services providing reliable site information for design, planning, development, and construction decisions.',
      features: [
        'Topographic surveys',
        'Boundary surveys',
        'ALTA / NSPS surveys',
        'Contour & surface mapping',
      ],
      image:
        'https://images.unsplash.com/photo-1531835551805-16d864c8d311?auto=format&fit=crop&w=1400&q=85',
      accent: '#06b6d4',
      stat: '04',
      label: 'MEASURE • MAP • VERIFY',
    },

    {
      id: '05',
      category: 'CIVIL ENGINEERING',
      title: 'Civil Drafting',
      shortTitle: 'Civil Drafting',
      description:
        'Detailed CAD and Civil 3D drafting services that convert engineering concepts and survey information into clear, accurate construction documentation.',
      features: [
        'AutoCAD civil drafting',
        'Civil 3D documentation',
        'Site layout drawings',
        'Construction plan preparation',
      ],
      image:
        'https://images.unsplash.com/photo-1581094271901-8022df4466f9?auto=format&fit=crop&w=1400&q=85',
      accent: '#0284c7',
      stat: '05',
      label: 'DRAFT • DETAIL • DOCUMENT',
    },
  ];

  const active = servicesList[activeService];

  const styles = {
    section: {
      position: 'relative',
      minHeight: '100vh',
      width: '100%',
      padding: '90px 5%',
      backgroundColor: colors.bg,
      overflow: 'hidden',
      fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      boxSizing: 'border-box',
    },

    backgroundGrid: {
      position: 'absolute',
      inset: 0,
      backgroundImage: `
        linear-gradient(rgba(2,132,199,0.035) 1px, transparent 1px),
        linear-gradient(90deg, rgba(2,132,199,0.035) 1px, transparent 1px)
      `,
      backgroundSize: '45px 45px',
      animation: 'engineeringGrid 30s linear infinite',
      pointerEvents: 'none',
    },

    glowOne: {
      position: 'absolute',
      width: '420px',
      height: '420px',
      borderRadius: '50%',
      background: 'rgba(56,189,248,0.10)',
      filter: 'blur(80px)',
      top: '-150px',
      right: '-100px',
      animation: 'floatingGlow 8s ease-in-out infinite',
      pointerEvents: 'none',
    },

    glowTwo: {
      position: 'absolute',
      width: '300px',
      height: '300px',
      borderRadius: '50%',
      background: 'rgba(2,132,199,0.08)',
      filter: 'blur(70px)',
      bottom: '-120px',
      left: '-80px',
      animation: 'floatingGlowTwo 10s ease-in-out infinite',
      pointerEvents: 'none',
    },

    container: {
      position: 'relative',
      zIndex: 2,
      maxWidth: '1320px',
      margin: '0 auto',
    },

    topHeader: {
      display: 'grid',
      gridTemplateColumns: '1fr 1.2fr',
      gap: '60px',
      alignItems: 'end',
      marginBottom: '55px',
    },

    eyebrow: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      color: colors.blueDark,
      fontSize: '0.75rem',
      fontWeight: '800',
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      marginBottom: '18px',
    },

    eyebrowLine: {
      width: '35px',
      height: '2px',
      backgroundColor: colors.blue,
      display: 'inline-block',
    },

    heading: {
      margin: 0,
      fontSize: 'clamp(2.5rem, 5vw, 5rem)',
      lineHeight: '0.98',
      letterSpacing: '-0.055em',
      fontWeight: '850',
      color: colors.navy,
    },

    headingAccent: {
      color: colors.blue,
      position: 'relative',
      display: 'inline-block',
    },

    headingAccentLine: {
      position: 'absolute',
      left: 0,
      bottom: '-8px',
      width: '80%',
      height: '5px',
      background: 'linear-gradient(90deg, #0284c7, #38bdf8)',
      borderRadius: '10px',
      animation: 'lineGrow 2s ease forwards',
    },

    headerDescription: {
      color: colors.muted,
      fontSize: '1.05rem',
      lineHeight: '1.75',
      maxWidth: '600px',
      margin: '0 0 5px auto',
    },

    mainShowcase: {
      display: 'grid',
      gridTemplateColumns: '0.75fr 1.8fr',
      gap: '28px',
      alignItems: 'stretch',
    },

    navigation: {
      backgroundColor: 'rgba(255,255,255,0.88)',
      border: `1px solid ${colors.border}`,
      borderRadius: '24px',
      padding: '15px',
      boxShadow: '0 25px 60px rgba(15,23,42,0.07)',
      backdropFilter: 'blur(15px)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },

    navItem: (activeItem) => ({
      position: 'relative',
      padding: '24px 20px',
      borderRadius: '16px',
      cursor: 'pointer',
      backgroundColor: activeItem ? colors.soft : 'transparent',
      border: activeItem
        ? `1px solid rgba(2,132,199,0.16)`
        : '1px solid transparent',
      transition: 'all 0.35s ease',
      overflow: 'hidden',
    }),

    navActiveBar: {
      position: 'absolute',
      left: 0,
      top: '18px',
      bottom: '18px',
      width: '4px',
      background: 'linear-gradient(180deg, #0284c7, #38bdf8)',
      borderRadius: '0 5px 5px 0',
    },

    navNumber: (activeItem) => ({
      fontSize: '0.72rem',
      fontWeight: '800',
      color: activeItem ? colors.blue : '#94a3b8',
      letterSpacing: '0.1em',
      transition: 'all 0.3s ease',
    }),

    navTitle: (activeItem) => ({
      marginTop: '8px',
      fontSize: '1.05rem',
      lineHeight: '1.35',
      fontWeight: '750',
      color: activeItem ? colors.navy : colors.text,
      transition: 'all 0.3s ease',
    }),

    navArrow: (activeItem) => ({
      position: 'absolute',
      right: '18px',
      top: '50%',
      transform: activeItem
        ? 'translateY(-50%) translateX(0)'
        : 'translateY(-50%) translateX(-8px)',
      opacity: activeItem ? 1 : 0,
      color: colors.blue,
      fontSize: '1.2rem',
      transition: 'all 0.3s ease',
    }),

    showcase: {
      position: 'relative',
      minHeight: '610px',
      borderRadius: '28px',
      overflow: 'hidden',
      backgroundColor: colors.navy,
      boxShadow: '0 30px 70px rgba(15,23,42,0.16)',
    },

    showcaseImage: (hovered) => ({
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transform: hovered ? 'scale(1.07)' : 'scale(1)',
      transition: 'transform 1.2s cubic-bezier(0.2,0.7,0.2,1)',
      filter: 'brightness(0.68) saturate(0.9)',
    }),

    imageOverlay: {
      position: 'absolute',
      inset: 0,
      background: `
        linear-gradient(
          90deg,
          rgba(7,25,42,0.96) 0%,
          rgba(7,25,42,0.80) 42%,
          rgba(7,25,42,0.18) 100%
        ),
        linear-gradient(
          0deg,
          rgba(7,25,42,0.85) 0%,
          transparent 60%
        )
      `,
    },

    imageGrid: {
      position: 'absolute',
      inset: 0,
      opacity: 0.12,
      backgroundImage: `
        linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
      `,
      backgroundSize: '50px 50px',
      animation: 'imageGridMove 20s linear infinite',
    },

    showcaseContent: {
      position: 'relative',
      zIndex: 4,
      minHeight: '610px',
      padding: '55px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },

    serviceTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '20px',
    },

    serviceCategory: {
      color: '#7dd3fc',
      fontSize: '0.72rem',
      fontWeight: '800',
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
    },

    giantNumber: {
      fontSize: '5rem',
      fontWeight: '900',
      lineHeight: 0.8,
      color: 'rgba(255,255,255,0.10)',
      letterSpacing: '-0.08em',
    },

    serviceBottom: {
      maxWidth: '720px',
    },

    serviceTitle: {
      color: '#ffffff',
      fontSize: 'clamp(2rem, 4vw, 3.7rem)',
      lineHeight: '1.05',
      letterSpacing: '-0.04em',
      fontWeight: '800',
      margin: '0 0 18px',
      animation: 'serviceTextIn 0.5s ease',
    },

    serviceDescription: {
      color: 'rgba(255,255,255,0.76)',
      fontSize: '1rem',
      lineHeight: '1.7',
      maxWidth: '650px',
      margin: 0,
    },

    featureRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      marginTop: '28px',
    },

    feature: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '7px',
      padding: '9px 13px',
      borderRadius: '999px',
      backgroundColor: 'rgba(255,255,255,0.09)',
      border: '1px solid rgba(255,255,255,0.13)',
      color: 'rgba(255,255,255,0.88)',
      fontSize: '0.78rem',
      fontWeight: '600',
      backdropFilter: 'blur(10px)',
    },

    bottomInfo: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '30px',
      marginTop: '40px',
    },

    processLabel: {
      color: 'rgba(255,255,255,0.55)',
      fontSize: '0.7rem',
      fontWeight: '700',
      letterSpacing: '0.15em',
    },

    processText: {
      color: '#ffffff',
      fontSize: '0.85rem',
      fontWeight: '700',
      marginTop: '5px',
    },

    progressOuter: {
      width: '180px',
      height: '3px',
      backgroundColor: 'rgba(255,255,255,0.15)',
      borderRadius: '10px',
      overflow: 'hidden',
    },

    progressInner: {
      height: '100%',
      width: `${((activeService + 1) / servicesList.length) * 100}%`,
      background: 'linear-gradient(90deg, #0284c7, #38bdf8)',
      transition: 'width 0.6s ease',
    },

    floatingBadge: {
      position: 'absolute',
      right: '35px',
      bottom: '35px',
      zIndex: 6,
      padding: '14px 17px',
      backgroundColor: 'rgba(255,255,255,0.94)',
      borderRadius: '14px',
      boxShadow: '0 15px 40px rgba(0,0,0,0.18)',
      backdropFilter: 'blur(10px)',
      animation: 'badgeFloat 4s ease-in-out infinite',
    },

    badgeTop: {
      color: colors.blue,
      fontSize: '0.65rem',
      fontWeight: '800',
      letterSpacing: '0.12em',
    },

    badgeBottom: {
      color: colors.navy,
      fontSize: '0.78rem',
      fontWeight: '800',
      marginTop: '4px',
    },

    bottomStrip: {
      marginTop: '30px',
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '1px',
      borderRadius: '18px',
      overflow: 'hidden',
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.border,
    },

    stripItem: {
      backgroundColor: 'rgba(255,255,255,0.88)',
      padding: '20px',
      textAlign: 'center',
    },

    stripNumber: {
      fontSize: '1.4rem',
      fontWeight: '850',
      color: colors.navy,
    },

    stripLabel: {
      fontSize: '0.7rem',
      color: colors.muted,
      fontWeight: '700',
      letterSpacing: '0.08em',
      marginTop: '3px',
    },
    engineeringGrid: {
      position: 'absolute',
      inset: 0,
      backgroundImage: `
        linear-gradient(
          rgba(8,121,209,0.045) 1px,
          transparent 1px
        ),
        linear-gradient(
          90deg,
          rgba(8,121,209,0.045) 1px,
          transparent 1px
        )
      `,
      backgroundSize: '48px 48px',
      animation: 'aboutGridMove 24s linear infinite',
      pointerEvents: 'none',
      zIndex: 0,
    },
    dotGrid: {
      position: 'absolute',
      inset: 0,
      backgroundImage:
        'radial-gradient(rgba(8,121,209,0.17) 1px, transparent 1px)',
      backgroundSize: '25px 25px',
      opacity: 0.25,
      animation: 'aboutDotMove 18s linear infinite',
      pointerEvents: 'none',
      zIndex: 0,
    },

  };

  return (
    <section style={styles.section}>
      <style>{`
        @keyframes engineeringGrid {
          0% {
            background-position: 0 0, 0 0;
          }
          100% {
            background-position: 45px 45px, 45px 45px;
          }
        }

        @keyframes imageGridMove {
          0% {
            background-position: 0 0, 0 0;
          }
          100% {
            background-position: 50px 50px, 50px 50px;
          }
        }

        @keyframes floatingGlow {
          0%,100% {
            transform: translate(0,0) scale(1);
          }
          50% {
            transform: translate(-30px,30px) scale(1.08);
          }
        }

        @keyframes floatingGlowTwo {
          0%,100% {
            transform: translate(0,0);
          }
          50% {
            transform: translate(30px,-25px);
          }
        }

        @keyframes badgeFloat {
          0%,100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }

        @keyframes lineGrow {
          from {
            width: 0;
          }
          to {
            width: 80%;
          }
        }

        @keyframes serviceTextIn {
          from {
            opacity: 0;
            transform: translateY(15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 1050px) {
          .services-header {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }

          .services-header-description {
            margin-left: 0 !important;
          }

          .services-showcase {
            grid-template-columns: 1fr !important;
          }

         .services-navigation {
  display: grid !important;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}

          .services-nav-item {
            min-height: 120px;
          }
        }

        @media (max-width: 700px) {
          .services-section {
            padding: 65px 18px !important;
          }

          .services-header {
            margin-bottom: 35px !important;
          }

          .services-navigation {
            display: flex !important;
            flex-direction: row !important;
            overflow-x: auto;
            padding: 8px !important;
          }

          .services-nav-item {
            min-width: 220px;
            min-height: auto !important;
          }

          .services-showcase {
            min-height: 590px !important;
          }

          .services-showcase-content {
            min-height: 590px !important;
            padding: 30px 25px !important;
          }

          .services-giant-number {
            font-size: 3.5rem !important;
          }

          .services-title {
            font-size: 2.15rem !important;
          }

          .services-description {
            font-size: 0.92rem !important;
          }

          .services-floating-badge {
            display: none !important;
          }

          .services-bottom-info {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 18px !important;
          }

          .services-progress {
            width: 140px !important;
          }

          .services-bottom-strip {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }

        @media (max-width: 420px) {
          .services-title {
            font-size: 1.85rem !important;
          }

          .services-showcase-content {
            padding: 25px 20px !important;
          }

          .services-feature {
            font-size: 0.7rem !important;
          }
        }
      `}</style>

      {/* <div style={styles.backgroundGrid} /> */}


      <div style={styles.engineeringGrid} />
      <div style={styles.dotGrid} />
      <div style={styles.glowOne} />
      <div style={styles.glowTwo} />

      <div
        style={styles.container}
        className="services-section"
      >

        {/* HEADER */}
        <div
          style={styles.topHeader}
          className="services-header"
        >
          <div>
            <div style={styles.eyebrow}>
              <span style={styles.eyebrowLine} />
              What We Engineer
            </div>

            <h2 style={styles.heading}>
              Engineering
              <br />
              <span style={styles.headingAccent}>
                Capabilities
                <span style={styles.headingAccentLine} />
              </span>
            </h2>
          </div>

          <p
            style={styles.headerDescription}
            className="services-header-description"
          >
            From structural engineering and digital modeling to Land Development,
            Land Survey, and Civil Drafting, our multidisciplinary capabilities
            connect technical intelligence with accurate, real-world construction
            documentation.
          </p>
        </div>

        {/* MAIN SHOWCASE */}
        <div
          style={styles.mainShowcase}
          className="services-showcase"
        >

          {/* LEFT NAVIGATION */}
          <div
            style={styles.navigation}
            className="services-navigation"
          >
            {servicesList.map((service, index) => {
              const isActive = activeService === index;

              return (
                <div
                  key={service.id}
                  style={styles.navItem(isActive)}
                  className="services-nav-item"
                  onMouseEnter={() => setActiveService(index)}
                  onClick={() => setActiveService(index)}
                >

                  {isActive && (
                    <div style={styles.navActiveBar} />
                  )}

                  <div style={styles.navNumber(isActive)}>
                    {service.id} / {String(servicesList.length).padStart(2, '0')}
                  </div>

                  <div style={styles.navTitle(isActive)}>
                    {service.shortTitle}
                  </div>

                  <div style={styles.navArrow(isActive)}>
                    →
                  </div>

                </div>
              );
            })}
          </div>

          {/* LARGE VISUAL PANEL */}
          <div
            style={styles.showcase}
            onMouseEnter={() => setImageHover(true)}
            onMouseLeave={() => setImageHover(false)}
          >

            <img
              key={active.image}
              src={active.image}
              alt={active.title}
              style={styles.showcaseImage(imageHover)}
            />

            <div style={styles.imageOverlay} />

            <div style={styles.imageGrid} />

            <div
              style={styles.showcaseContent}
              className="services-showcase-content"
            >

              {/* TOP */}
              <div style={styles.serviceTop}>

                <div>
                  <div style={styles.serviceCategory}>
                    {active.category}
                  </div>

                  <div
                    style={{
                      width: '45px',
                      height: '2px',
                      backgroundColor: '#38bdf8',
                      marginTop: '12px',
                    }}
                  />
                </div>

                <div
                  style={styles.giantNumber}
                  className="services-giant-number"
                >
                  {active.stat}
                </div>

              </div>

              {/* BOTTOM */}
              <div
                style={styles.serviceBottom}
                key={active.id}
              >

                <h3
                  style={styles.serviceTitle}
                  className="services-title"
                >
                  {active.title}
                </h3>

                <p
                  style={styles.serviceDescription}
                  className="services-description"
                >
                  {active.description}
                </p>

                <div style={styles.featureRow}>
                  {active.features.map((feature, index) => (
                    <span
                      key={index}
                      style={styles.feature}
                      className="services-feature"
                    >
                      <span style={{ color: '#38bdf8' }}>
                        ✓
                      </span>
                      {feature}
                    </span>
                  ))}
                </div>

                <div
                  style={styles.bottomInfo}
                  className="services-bottom-info"
                >

                  <div>
                    <div style={styles.processLabel}>
                      ENGINEERING PROCESS
                    </div>

                    <div style={styles.processText}>
                      {active.label}
                    </div>
                  </div>

                  <div>
                    <div style={styles.progressOuter}>
                      <div
                        style={styles.progressInner}
                        className="services-progress"
                      />
                    </div>
                  </div>

                </div>

              </div>
            </div>

            {/* FLOATING BADGE */}
            <div
              style={styles.floatingBadge}
              className="services-floating-badge"
            >
              <div style={styles.badgeTop}>
                SANSA ENGINEERING
              </div>

              <div style={styles.badgeBottom}>
                Precision • Safety • Performance
              </div>
            </div>

          </div>
        </div>

        {/* BOTTOM STATISTICS */}
        {/* <div
          style={styles.bottomStrip}
          className="services-bottom-strip"
        >

          <div style={styles.stripItem}>
            <div style={styles.stripNumber}>03+</div>
            <div style={styles.stripLabel}>
              ENGINEERING VERTICALS
            </div>
          </div>

          <div style={styles.stripItem}>
            <div style={styles.stripNumber}>25+</div>
            <div style={styles.stripLabel}>
              YEARS DIRECTOR EXPERIENCE
            </div>
          </div>

          <div style={styles.stripItem}>
            <div style={styles.stripNumber}>3D</div>
            <div style={styles.stripLabel}>
              DIGITAL WORKFLOWS
            </div>
          </div>

          <div style={styles.stripItem}>
            <div style={styles.stripNumber}>100%</div>
            <div style={styles.stripLabel}>
              ENGINEERING FOCUS
            </div>
          </div>

        </div> */}

        <div
          style={styles.bottomStrip}
          className="services-bottom-strip"
        >
          <div style={styles.stripItem}>
            <div style={styles.stripNumber}>05</div>
            <div style={styles.stripLabel}>
              ENGINEERING VERTICALS
            </div>
          </div>

          <div style={styles.stripItem}>
            <div style={styles.stripNumber}>25+</div>
            <div style={styles.stripLabel}>
              YEARS DIRECTOR EXPERIENCE
            </div>
          </div>

          <div style={styles.stripItem}>
            <div style={styles.stripNumber}>3D</div>
            <div style={styles.stripLabel}>
              DIGITAL WORKFLOWS
            </div>
          </div>

          <div style={styles.stripItem}>
            <div style={styles.stripNumber}>100%</div>
            <div style={styles.stripLabel}>
              ENGINEERING FOCUS
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Services;